import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import { useThemeStore } from '../../stores/themeStore'
import { useUIStore } from '../../stores/uiStore'
import api from '../../lib/api'
import type { Doctor, WorkOrder, Invoice, PaginatedResponse } from '../../types'

interface GlobalSearchResult {
  id: string
  type: 'doctor' | 'work_order' | 'invoice'
  title: string
  subtitle: string
  url: string
}

export default function Navbar() {
  const { user, logout } = useAuthStore()
  const { theme, setTheme } = useThemeStore()
  const { toggleSidebar } = useUIStore()
  const navigate = useNavigate()

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [themeMenuOpen, setThemeMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const themeMenuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
      if (themeMenuRef.current && !themeMenuRef.current.contains(event.target as Node)) {
        setThemeMenuOpen(false)
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch global search data
  const { data: searchResults = [] } = useQuery({
    queryKey: ['global-search'],
    queryFn: async (): Promise<GlobalSearchResult[]> => {
      try {
        const [doctorsRes, ordersRes, invoicesRes] = await Promise.all([
          api.get<PaginatedResponse<Doctor>>('/doctors?limit=20'),
          api.get<PaginatedResponse<WorkOrder>>('/work-orders?limit=20'),
          api.get<PaginatedResponse<Invoice>>('/invoices?limit=20'),
        ])
        
        const results: GlobalSearchResult[] = []
        
        doctorsRes.data.data?.forEach(doctor => {
          results.push({
            id: doctor.id,
            type: 'doctor',
            title: doctor.name,
            subtitle: doctor.clinicName || doctor.phone,
            url: `/doctors/${doctor.id}`,
          })
        })
        
        ordersRes.data.data?.forEach(order => {
          results.push({
            id: order.id,
            type: 'work_order',
            title: order.displayId,
            subtitle: `${order.doctor?.name || 'Unknown'} • ${(order.status || '').replace('_', ' ')}`,
            url: `/work-orders/${order.id}`,
          })
        })
        
        invoicesRes.data.data?.forEach(invoice => {
          results.push({
            id: invoice.id,
            type: 'invoice',
            title: invoice.displayId,
            subtitle: invoice.doctor?.name || 'Unknown',
            url: `/invoices/${invoice.id}`,
          })
        })
        
        return results
      } catch {
        return []
      }
    },
    staleTime: 60000, // Cache for 1 minute
  })

  // Filter results based on search query
  const filteredResults = searchResults.filter(result => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return result.title.toLowerCase().includes(query) || 
           result.subtitle.toLowerCase().includes(query)
  }).slice(0, 8)

  const handleSearchSelect = (result: GlobalSearchResult) => {
    navigate(result.url)
    setSearchOpen(false)
    setSearchQuery('')
    setHighlightedIndex(-1)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!searchOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setSearchOpen(true)
        return
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => prev < filteredResults.length - 1 ? prev + 1 : 0)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredResults.length - 1)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredResults[highlightedIndex]) {
          handleSearchSelect(filteredResults[highlightedIndex])
        }
        break
      case 'Escape':
        setSearchOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }

  const getTypeIcon = (type: GlobalSearchResult['type']) => {
    switch (type) {
      case 'doctor': return 'fa-user-md'
      case 'work_order': return 'fa-clipboard-list'
      case 'invoice': return 'fa-file-invoice'
      default: return 'fa-circle'
    }
  }

  const getTypeColor = (type: GlobalSearchResult['type']) => {
    switch (type) {
      case 'doctor': return 'text-blue-500 bg-blue-100 dark:bg-blue-900/30'
      case 'work_order': return 'text-amber-500 bg-amber-100 dark:bg-amber-900/30'
      case 'invoice': return 'text-green-500 bg-green-100 dark:bg-green-900/30'
      default: return 'text-gray-500 bg-gray-100 dark:bg-gray-700'
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const themes = [
    { id: 'light' as const, name: 'Light', icon: 'fa-sun' },
    { id: 'dark' as const, name: 'Dark', icon: 'fa-moon' },
  ]

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-40 shadow-global">
      <div className="h-full px-4 flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            aria-label="Toggle menu"
          >
            <i className="fas fa-bars text-lg"></i>
          </button>

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-600 text-white rounded-lg flex items-center justify-center">
              <i className="fas fa-tooth"></i>
            </div>
            <span className="font-bold text-lg text-gray-900 dark:text-white">
              DentaLabs
            </span>
          </Link>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Search (desktop) */}
          <div className="hidden md:block relative" ref={searchRef}>
            <div className="relative">
              <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search doctors, orders, invoices..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setHighlightedIndex(-1) }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={handleSearchKeyDown}
                className="w-72 pl-11 pr-4 py-2 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 placeholder:text-gray-400"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); searchInputRef.current?.focus() }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
            
            {searchOpen && filteredResults.length > 0 && (
              <div className="absolute top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-auto z-50">
                {filteredResults.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSearchSelect(result)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      highlightedIndex === index 
                        ? 'bg-primary-50 dark:bg-primary-900/30' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getTypeColor(result.type)}`}>
                      <i className={`fas ${getTypeIcon(result.type)} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {result.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {result.subtitle}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 capitalize">{(result.type || '').replace('_', ' ')}</span>
                  </button>
                ))}
              </div>
            )}
            
            {searchOpen && searchQuery && filteredResults.length === 0 && (
              <div className="absolute top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 text-center z-50">
                <i className="fas fa-search text-gray-300 dark:text-gray-600 text-2xl"></i>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  No results for "{searchQuery}"
                </p>
              </div>
            )}
          </div>

          {/* Theme selector */}
          <div className="relative" ref={themeMenuRef}>
            <button
              onClick={() => setThemeMenuOpen(!themeMenuOpen)}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              aria-label="Change theme"
            >
              <i className={`fas ${themes.find(t => t.id === theme)?.icon || 'fa-sun'}`}></i>
            </button>

            {themeMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 animate-fade-in">
                {themes.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTheme(t.id)
                      setThemeMenuOpen(false)
                    }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      theme === t.id ? 'text-primary-600 font-medium' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <i className={`fas ${t.icon} w-4`}></i>
                    {t.name}
                    {theme === t.id && <i className="fas fa-check ml-auto"></i>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-300">
                {user?.name || 'User'}
              </span>
              <i className="fas fa-chevron-down text-xs text-gray-400"></i>
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 animate-fade-in">
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <p className="font-medium text-gray-900 dark:text-white">{user?.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                  <span className={`inline-block mt-1 badge ${
                    user?.role === 'admin' ? 'badge-error' : 'badge-info'
                  }`}>
                    {user?.role}
                  </span>
                </div>

                <Link
                  to="/settings"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <i className="fas fa-cog w-4"></i>
                  Settings
                </Link>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <i className="fas fa-sign-out-alt w-4"></i>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
